import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { AiModelPrice, AiPrices, AiPricesService } from '../../service/ai/ai-prices.service';
import { AiChanges, AiIndexRow, AiPriceEvent, AiWatchService } from '../../service/ai/ai-watch.service';
import { aiPlatformIcon } from '../../util/ai-cost';
import {
  AI_OWN_APIS, AI_WATCH_DEFAULT_FIELD, AI_WATCH_DEFAULT_RANGE, AI_WATCH_FIELDS, AI_WATCH_RANGES, aiWatchCurve, aiWatchDays,
  AiWatchContext, AiWatchDay, aiWatchOrder, aiWatchPct, aiWatchPctLabel, aiWatchRangeDays, AiWatchField, AiWatchRange,
} from '../../util/ai-price-watch';

type Direction = 'down' | 'up' | 'flat';

/** a model in the ticker list or on the tape: its picked price and its change over the picked range */
interface Ticker {
  id: string;
  name: string;
  platform: string;
  price: number | null;
  direction: Direction;
  pct: number | null;
}

interface Chart {
  /** the line: its path, the area under it and the dashed level of the range's first price */
  line: string;
  area: string;
  baseY: number | null;
  /** where the line starts: the dashed level runs from there */
  startX: number;
  yTicks: { y: number; label: string }[];
  xTicks: { x: number; label: string }[];
  /** the line's points with their price and day, read back on hover */
  points: { x: number; y: number; price: string; day: string; note: string }[];
  bottom: number;
}

/** what the board reports after each change; `source` says which control moved */
export interface AiWatchBoardChange {
  model: string;
  field: AiWatchField;
  range: AiWatchRange;
  query: string;
  source: 'model' | 'field' | 'range' | 'search';
}

/**
 * The AI Price Watch board (owner 2026-10-28, "borsa hisseleri gibi"): the tickers on the left under a search box,
 * the picked model's price line on the right with the price and the range to pick above it, the tape of models
 * at the foot. It reads GET /ai/prices, /ai/changes and /ai/index itself; the model, the price, the range and the
 * search come in and every change goes out (boardChange), so the page keeps its url and the home box its state.
 * The tape runs out by --aw-bleed on each side, the padding of the card around the board, to the card's edges.
 */
@Component( {
  selector: 'app-ai-price-watch-widget',
  templateUrl: './ai-price-watch-widget.component.html',
  styleUrls: [ './ai-price-watch-widget.component.scss', '../../page/landing-layout/tool-pages/ai-cost-page/ai-cost-table.scss' ],
} )
export class AiPriceWatchWidgetComponent implements OnInit, OnChanges, OnDestroy {

  @Input() model = '';
  @Input() field: AiWatchField = AI_WATCH_DEFAULT_FIELD;
  @Input() range: AiWatchRange = AI_WATCH_DEFAULT_RANGE;
  @Input() query = '';
  /** the home's box: the same board, smaller, in the box's card shell, without the tape and the hint */
  @Input() @HostBinding( 'class.aw-compact-host' ) compact = false;
  @Output() boardChange = new EventEmitter<AiWatchBoardChange>();

  readonly fields = AI_WATCH_FIELDS;
  readonly ranges = AI_WATCH_RANGES;
  readonly CHART_W = 640;
  readonly CHART_H = 260;
  readonly CHART_AXIS = 56;
  /** the loading silhouette: a made-up price line as the outline of the area under it */
  readonly skeletonArea = 'polygon(0% 100%, ' + Array.from( { length: 25 }, ( _, i ) => {
    const top = 50 - Math.sin( i / 3.2 ) * 18 - Math.sin( i * 1.3 ) * 5;
    return `${ ( i * 100 / 24 ).toFixed( 1 ) }% ${ top.toFixed( 1 ) }%`;
  } ).join( ', ' ) + ', 100% 100%)';
  readonly skeletonRows = Array.from( { length: 9 }, ( _, i ) => i );

  changes: AiChanges | null = null;
  prices: AiPrices | null = null;
  indexRows: AiIndexRow[] = [];

  tickers: Ticker[] = [];
  ticks: Ticker[] = [];
  selected: AiModelPrice | null = null;
  selectedEvents: AiPriceEvent[] = [];
  chart: Chart = { line: '', area: '', baseY: null, yTicks: [], xTicks: [], points: [], bottom: 0, startX: 0 };
  /** the last move of the picked price over the range, on the line under the chart (one line, so the board keeps its height) */
  lastMove = '';
  /** the point under the pointer, null when it is off the chart */
  hover: Chart[ 'points' ][ number ] | null = null;
  chartPct: number | null = null;

  private subs: Subscription[] = [];

  constructor( private watch: AiWatchService, private priceService: AiPricesService ) {
  }

  ngOnInit(): void {
    this.subs.push( this.watch.changes$.subscribe( data => {
      this.changes = data;
      this.build();
    } ) );
    this.subs.push( this.priceService.prices$.subscribe( data => {
      this.prices = data;
      this.build();
    } ) );
    this.subs.push( this.watch.index$.subscribe( data => {
      this.indexRows = data.days;
      this.build();
    } ) );
  }

  ngOnChanges(): void {
    this.build();
  }

  ngOnDestroy(): void {
    this.subs.forEach( s => s.unsubscribe() );
  }

  // ---------------------------------------------------------------- words and numbers

  platformLabel( key: string ): string {
    return this.changes?.platforms[ key ]?.label || this.prices?.platforms[ key ]?.label || key;
  }

  platformIcon( key: string ): string {
    return aiPlatformIcon( key );
  }

  price( value: number | null ): string {
    return AiPricesService.price( value );
  }

  pctLabel( pct: number | null ): string {
    return aiWatchPctLabel( pct );
  }

  get fieldHint(): string {
    return ( AI_WATCH_FIELDS.find( f => f.key === this.field ) || AI_WATCH_FIELDS[ 0 ] ).hint;
  }

  get fieldName(): string {
    return ( AI_WATCH_FIELDS.find( f => f.key === this.field ) || AI_WATCH_FIELDS[ 0 ] ).label.toLowerCase();
  }

  fieldPrice( m: AiModelPrice ): number | null {
    return m[ this.field ];
  }

  // ---------------------------------------------------------------- picking

  setQuery( value: string ): void {
    this.query = value;
    this.emit( 'search' );
  }

  setRange( key: AiWatchRange ): void {
    this.range = key;
    this.emit( 'range' );
  }

  setField( key: AiWatchField ): void {
    this.field = key;
    this.emit( 'field' );
  }

  /** a ticker or a tape chip: that model on the chart */
  pickModel( id: string ): void {
    if ( this.model === id ) { return; }
    this.model = id;
    this.emit( 'model' );
  }

  private emit( source: AiWatchBoardChange[ 'source' ] ): void {
    this.build();
    this.boardChange.emit( { model: this.model, field: this.field, range: this.range, query: this.query, source } );
  }

  // ---------------------------------------------------------------- the board

  private get ctx(): AiWatchContext {
    return { field: this.field, firstDay: this.indexRows.length ? this.indexRows[ 0 ].day : '' };
  }

  /** the ticker list, the tape and the chart of the picked model; every change is read over the picked range */
  private build(): void {
    if ( !this.prices ) { return; }
    const span = aiWatchRangeDays( this.range ), ctx = this.ctx;
    const byModel: { [ id: string ]: AiPriceEvent[] } = {};
    ( this.changes?.events || [] ).forEach( e => ( byModel[ e.modelId ] = byModel[ e.modelId ] || [] ).push( e ) );
    const ticker = ( m: AiModelPrice ): Ticker => {
      const pct = aiWatchPct( m[ this.field ], aiWatchDays( m, byModel[ m.id ] || [], span, ctx ) );
      return { id: m.id, name: m.name, platform: m.platform, price: m[ this.field ], pct, direction: !pct ? 'flat' : pct < 0 ? 'down' : 'up' };
    };
    const all = this.prices.models.slice().sort( aiWatchOrder );
    const rows: { [ id: string ]: Ticker } = {};
    all.forEach( m => ( rows[ m.id ] = ticker( m ) ) );
    const q = this.query.trim().toLowerCase();
    // the list: the models that moved first, then the vendors' own APIs, then the rest
    this.tickers = all
      .filter( m => !q || `${ m.name } ${ m.vendor } ${ m.id } ${ this.platformLabel( m.platform ) }`.toLowerCase().indexOf( q ) >= 0 )
      .map( m => rows[ m.id ] )
      .sort( ( a, b ) => ( a.pct ? 0 : 1 ) - ( b.pct ? 0 : 1 ) );
    // the tape: the models that moved, the biggest move leading, then the rest of the vendors' own APIs
    const moved = all.filter( m => rows[ m.id ].pct ).map( m => rows[ m.id ] ).sort( ( a, b ) => Math.abs( b.pct || 0 ) - Math.abs( a.pct || 0 ) );
    const rest = all.filter( m => !rows[ m.id ].pct && AI_OWN_APIS.indexOf( m.platform ) >= 0 ).map( m => rows[ m.id ] );
    this.ticks = moved.concat( rest ).slice( 0, 48 );
    const picked = this.prices.models.find( m => m.id === this.model )
      || this.prices.models.find( m => m.id === this.tickers[ 0 ]?.id ) || null;
    this.selected = picked;
    if ( picked && !this.model ) { this.model = picked.id; }
    this.selectedEvents = picked ? byModel[ picked.id ] || [] : [];
    this.buildChart();
  }

  private buildChart(): void {
    const m = this.selected;
    this.chart = { line: '', area: '', baseY: null, yTicks: [], xTicks: [], points: [], bottom: 0, startX: 0 };
    this.hover = null;
    this.chartPct = null;
    if ( !m ) { return; }
    const daily = aiWatchDays( m, this.selectedEvents, aiWatchRangeDays( this.range ), this.ctx );
    if ( !daily.length ) { return; }
    const values = [ daily[ 0 ].open ].concat( daily.map( c => c.close ) );
    // the axis always starts at $0 (owner) and climbs in a round 1, 2 or 5 step to a step above the highest price
    // ($4.18 reads on $0, $2, $4, $6)
    const highest = Math.max.apply( null, values );
    const raw = ( highest || 1 ) / 3, mag = Math.pow( 10, Math.floor( Math.log10( raw ) ) );
    const tick = ( [ 1, 2, 5, 10 ].find( n => n * mag >= raw ) || 10 ) * mag;
    const round = ( v: number ): number => +v.toFixed( 10 );
    const min = 0;
    let max = round( Math.ceil( highest / tick ) * tick );
    if ( max <= highest ) { max = round( max + tick ); }
    const plotW = this.CHART_W - this.CHART_AXIS, top = 12, bottom = this.CHART_H - 28;
    const y = ( v: number ): number => +( bottom - ( v - min ) / ( max - min ) * ( bottom - top ) ).toFixed( 1 );
    const label = ( day: string ): string => new Date( day + 'T12:00:00Z' ).toLocaleDateString( 'en-US', { month: 'short', day: 'numeric' } );
    // the axis spans the whole range, a slot a day; the days before the watch's first price stay empty and the line
    // starts at that first price (the first day's open), then a point a day at its close, so a move on that day shows
    const span = Math.max( aiWatchRangeDays( this.range ), daily.length ), empty = span - daily.length;
    const step = plotW / span;
    const points = [ [ +( step * empty ).toFixed( 1 ), y( daily[ 0 ].open ) ] ]
      .concat( daily.map( ( c, i ) => [ +( step * ( empty + i + 1 ) ).toFixed( 1 ), y( c.close ) ] ) );
    // a day the price moved carries its note ("Up 100% from $2.09"), read in the hover label, never under the chart
    const note = ( c: AiWatchDay ): string => {
      if ( c.open === c.close ) { return ''; }
      const pct = Math.round( ( c.close - c.open ) / c.open * 100 );
      return `${ pct > 0 ? 'Up' : 'Down' } ${ Math.abs( pct ) }% from ${ this.price( c.open ) }`;
    };
    this.chart.points = [ { x: points[ 0 ][ 0 ], y: points[ 0 ][ 1 ], price: this.price( daily[ 0 ].open ), day: label( daily[ 0 ].day ), note: '' } ]
      .concat( daily.map( ( c, i ) => ( { x: points[ i + 1 ][ 0 ], y: points[ i + 1 ][ 1 ], price: this.price( c.close ), day: label( c.day ), note: note( c ) } ) ) );
    const moved = this.chart.points.filter( p => p.note ).pop();
    this.lastMove = moved ? `${ moved.note } on ${ moved.day }` : 'No price change in this range';
    this.chart.bottom = bottom;
    const curve = aiWatchCurve( points );
    this.chart.line = `M${ points[ 0 ].join( ',' ) }` + curve;
    this.chart.area = `M${ points[ 0 ][ 0 ] },${ bottom } L${ points[ 0 ].join( ',' ) }` + curve + ` L${ points[ points.length - 1 ][ 0 ] },${ bottom } Z`;
    this.chart.baseY = y( daily[ 0 ].open );
    this.chart.startX = points[ 0 ][ 0 ];
    // six dates or so across the range, today on the right
    const every = Math.max( 1, Math.ceil( span / 6 ) ), today = new Date( daily[ daily.length - 1 ].day + 'T00:00:00Z' );
    for ( let back = 0; back < span; back += every ) {
      const d = new Date( today.getTime() - back * 86400000 ).toISOString().slice( 0, 10 );
      this.chart.xTicks.unshift( { x: +( step * ( span - back ) ).toFixed( 1 ), label: label( d ) } );
    }
    for ( let v = min; v <= max + tick / 2; v = round( v + tick ) ) {
      this.chart.yTicks.push( { y: y( v ), label: tick >= 1 || v === 0 ? '$' + v : this.price( v ) } );
    }
    this.chartPct = aiWatchPct( m[ this.field ], daily );
  }

  /** the pointer over the plot: the day nearest to it, with its price */
  onPlotHover( event: PointerEvent ): void {
    const svg = event.currentTarget as SVGSVGElement;
    const ctm = svg.getScreenCTM();
    if ( !ctm || !this.chart.points.length ) { return; }
    const x = new DOMPoint( event.clientX, event.clientY ).matrixTransform( ctm.inverse() ).x;
    // the empty days before the first price have nothing to read
    if ( x < this.chart.startX - 8 ) { this.hover = null; return; }
    this.hover = this.chart.points.reduce( ( a, b ) => ( Math.abs( b.x - x ) < Math.abs( a.x - x ) ? b : a ) );
  }

  /** where the price label of the hovered point sits: above it, under it near the top, always inside the plot */
  hoverBox(): { x: number; y: number; w: number; h: number } {
    if ( !this.hover ) { return { x: 0, y: 0, w: 0, h: 0 }; }
    const w = Math.max( ( this.hover.price.length + this.hover.day.length ) * 6.6 + 30, this.hover.note.length * 6.2 + 20 );
    const h = this.hover.note ? 44 : 26;
    const x = Math.min( Math.max( this.hover.x - w / 2, 0 ), this.CHART_W - this.CHART_AXIS - w );
    const y = this.hover.y - h - 10 >= 0 ? this.hover.y - h - 10 : this.hover.y + 10;
    return { x, y, w, h };
  }
}
